import {useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {useImageUpload} from "../../utils/useImageUpload";
import AssetPackGrid from "./assetPackGrid.view";

interface AssetPackPopupProps {
    packId: string;
    // The image key the user clicked (the entity's representative image). Used
    // by the "Edit in Images tab" button so the tab lands on that exact key.
    mainKey: string;
    onClose(): void;
}

// Streamlined image-editing popup for an entity's asset pack. Clicking a filled
// slot opens a file dialog and replaces that image in place (the old file is
// archived automatically); clicking a '+' slot uploads a new image into it. A
// button hands off to the full Images tab for everything else (pick existing,
// archive restore, pivot/scale, remove). See useImageUpload for the upload path.
function AssetPackPopup(props: AssetPackPopupProps) {
    const {navigateToImage, getLocalizationValue} = useApp();
    const {state} = useIntegrator();
    const {applyUpload} = useImageUpload();
    const [loading, setLoading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Which slot the pending file-open dialog is targeting. A ref (not state) so
    // the onChange that fires after the dialog reads the slot set on click,
    // free of any render-timing race.
    const pendingSlotRef = useRef<{level: number; variation: number; key?: string} | undefined>(undefined);

    const hasPack = !!state.imagePacks?.[props.packId];

    function onUploadSlot(slot: {level: number; variation: number; key?: string}) {
        pendingSlotRef.current = slot;
        fileInputRef.current?.click();
    }

    async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = '';   // let the same file be re-picked later
        const slot = pendingSlotRef.current;
        pendingSlotRef.current = undefined;
        if (!file || !slot) return;
        // currentKey set → replace-in-place (old → archive); undefined → add to
        // the empty slot. Both handled by applyUpload.
        await applyUpload(
            {kind: 'packSlot', packId: props.packId, level: slot.level, variation: slot.variation},
            slot.key, file, setLoading);
    }

    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 12, maxWidth: '90vw', maxHeight: '85vh', overflow: 'hidden'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Asset pack — {getLocalizationValue(props.packId)}</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                <p className={'muted-text'} style={{fontSize: 12}}>
                    Click an image to replace it (the old one is archived), or a
                    <span className={'fa-solid fa-plus'} style={{margin: '0 5px', fontSize: 10}}/>
                    slot to add a new one.
                </p>

                <div style={{overflow: 'auto', minHeight: 0}}>
                    {hasPack
                        ? <AssetPackGrid packId={props.packId}
                                         onUploadSlot={onUploadSlot}
                                         allowRemove={false}/>
                        : <p className={'muted-text'} style={{fontSize: 13}}>
                            This entity has no asset pack yet — open it in the Images tab to set one up.
                          </p>}
                </div>

                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    {loading
                        ? <p style={{color: 'rgba(255,255,255,0.66)'}}>Uploading…</p>
                        : <span/>}
                    <button onClick={() => { navigateToImage(props.mainKey); props.onClose(); }}>
                        <span className={'fa-solid fa-up-right-from-square'} style={{marginRight: 6}}/>
                        Edit in Images tab
                    </button>
                </div>

                <input ref={fileInputRef} type={'file'} accept={'image/*'}
                       style={{display: 'none'}} onChange={onFileChosen}/>
            </div>
        </div>,
        document.body,
    );
}

export default AssetPackPopup;
